/**
 * ISG Utilities — Frontend Entry Point
 *
 * Hub-style app: utility registry with categories, search, admin.
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import '@isg/ui-kit/css/deck-studio.css';
import './utilities.css';

// --- Services & Utils ---
import { router, store, auth, checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { t, initI18n, onLocaleChange } from './i18n/index.js';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

// --- Lazy-loaded pages ---
const pages = {
  hub:                    () => import('./pages/hub.js'),
  login:                  () => import('./pages/login.js'),
  stockReport:            () => import('./pages/stock-report.js'),
  category:               () => import('./pages/category.js'),
  admin:                  () => import('./pages/admin.js'),
  presentations:          () => import('./pages/presentations.js'),
  presentationBuilder:    () => import('./pages/presentation-builder.js'),
  presentationView:       () => import('./pages/presentation-view.js'),
  shippingCalculator:     () => import('./pages/shipping-calculator.js'),
  decks:                  () => import('./pages/decks.js'),
  deckEditor:             () => import('./pages/deck-editor.js'),
};

// ── Build nav config ────────────────────────────────────────────────
// Built at render time (never at module scope) so labels follow the locale.
function buildNavConfig() {
  const user = store.get('user') || {};
  const isAdmin = user.role === 'admin'
    || (user.app_access?.utilities?.role === 'admin');

  const config = [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.all_tools'), icon: 'grid' },
      ],
    },
  ];

  // Admin section
  if (isAdmin) {
    config.push({
      section: t('nav.admin'),
      items: [
        { path: '/admin', label: t('nav.manage_utilities'), icon: 'settings' },
      ],
    });
  }

  return config;
}

// ── Render a page module into #main-content ─────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.something_went_wrong')}</p>
          <p>${err.message || t('page.load_failed')}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">${t('app.reload')}</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ───────────────────────────────────────────────────────
let sidebarEl = null;

function renderAppShell(navConfig) {
  const app = document.getElementById('app');
  app.innerHTML = '';

  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: t('app.title') });
  const overlay = createSidebarOverlay();

  sidebarEl = renderNavigation({
    navConfig,
    appTitle: t('app.title'),
    fallbackInitial: 'U',
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebarEl);
  layout.appendChild(main);
  app.appendChild(layout);

}

// ── Init ────────────────────────────────────────────────────────────
async function init() {
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // Boot i18n with the signed-in user's stored locale BEFORE any shell or
  // page renders (signed out: null, which falls back to the browser/default).
  await initI18n(hasSession ? store.get('user') : null).catch(() => {});

  // index.html's static <title> is English (painted before the locale is
  // known); from here on the tab is named in the reader's language.
  document.title = t('app.title');
  onLocaleChange(() => { document.title = t('app.title'); });

  // Define routes
  router
    .on('/', (params) => renderPage(pages.hub, params))
    .on('/login', (params) => renderPage(pages.login, params))
    .on('/stock-report', (params) => renderPage(pages.stockReport, params))
    .on('/category/:slug', (params) => renderPage(pages.category, params))
    .on('/admin', (params) => renderPage(pages.admin, params))
    .on('/presentations', (params) => renderPage(pages.presentations, params))
    .on('/presentations/new', (params) => renderPage(pages.presentationBuilder, params))
    .on('/presentations/:id/edit', (params) => renderPage(pages.presentationBuilder, params))
    .on('/presentations/:id/view', (params) => renderPage(pages.presentationView, params))
    .on('/presentations/share/:token', (params) => renderPage(pages.presentationView, params))
    .on('/shipping-calculator', (params) => renderPage(pages.shippingCalculator, params))
    .on('/decks', (params) => renderPage(pages.decks, params))
    .on('/decks/:id', (params) => renderPage(pages.deckEditor, params));

  // Auth guard
  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (path === '/login') return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    renderAppShell(buildNavConfig());
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('app.load_failed')}</p>
      <button class="btn btn-primary" onclick="location.reload()">${t('app.reload')}</button>
    </div>
  `;
});
